import { styled } from 'baseui';
import { Theme } from '../../Themes/themeProvider/ThemeProvider';
import { borderColor, borderStyle, borderWidth, marginSize, paddingSize } from '../../Utilities/utils/Theming';
import { IDummyProps } from './types';
import { focusRing, focusRingNoGap } from '../../Themes/base/focusRing';

export const CheckBoxFontSize: any = {
	mini: 'var(--epods--checkbox-label-mini-font-size)',
	compact: 'var(--epods--checkbox-label-compact-font-size)',
	default: 'var(--epods--checkbox-label-default-font-size)',
	large: 'var(--epods--checkbox-label-large-font-size)',
};

export const CheckBoxLineHeight: any = {
	mini: 'var(--epods--checkbox-label-mini-line-height)',
	compact: 'var(--epods--checkbox-label-compact-line-height)',
	default: 'var(--epods--checkbox-label-default-line-height)',
	large: 'var(--epods--checkbox-label-large-line-height)',
};

export const CaptionFontSize: any = {
	mini: 'var(--epods--checkbox-caption-default-size)',
	compact: 'var(--epods--checkbox-caption-default-size)',
	default: 'var(--epods--checkbox-caption-default-size)',
	large: 'var(--epods--checkbox-caption-large-size)',
};

export const CaptionLineHeight: any = {
	mini: 'var(--epods--checkbox-caption-default-line-height)',
	compact: 'var(--epods--checkbox-caption-default-line-height)',
	default: 'var(--epods--checkbox-caption-default-line-height)',
	large: 'var(--epods--checkbox-caption-large-line-height)',
};

export const CheckboxDescription = styled<any, 'div', any>('div', ({ $disabled, $theme, labelplacement, size }) => {
	return {
		marginLeft:
			labelplacement === 'left'
				? 'var(--epods--checkbox-label-horizontal-padding)'
				: 'var(--epods--checkbox-caption-margin)',
		marginBottom: 'var(--epods--space-inset-s)',
		marginRight: labelplacement === 'left' ? '42px' : 'var(--epods--checkbox-label-horizontal-padding)',
		textAlign: labelplacement === 'left' ? 'right' : 'left',

		lineHeight: CaptionLineHeight[size || 'default'],
		fontFamily: 'var(--epods--font-base)',
		fontSize: CaptionFontSize[size || 'default'], //$theme.checkbox.descriptionSize,

		color: $disabled ? 'var(--epods--color-text-disabled)' : 'var(--epods--checkbox-caption-color)',
	};
});

interface ICheckboxIcon {
	children: any;
	className: string;
}

export const CheckboxIcon = (theme: any) => {
	return {
		fill: 'var(--epods--color-text-inverted)',
		color: 'var(--epods--color-text-inverted)',
		fontSize: '16px',
		width: '16px',
		height: '16px',
	};
};

interface ICheckboxCustomIcon {
	isfocused: string;
	disabled: boolean;
	children: any;
	className: string;
	checkmarkiconactive?: any;
	checked?: boolean;
}

export const CheckboxCustomIcon = styled<ICheckboxCustomIcon, 'i', any>(
	'i',
	({ checked, checkmarkiconactive, isfocused, disabled, $theme }) => {
		const colorStatus = checked
			? checkmarkiconactive || $theme.colors.tickFillSelected
			: 'var(--epods--checkbox-tickFill)';
		return {
			width: '16px',
			height: 'var(--epods--line-height-p)',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			color: disabled ? $theme.colors.tickFillDisabled : colorStatus,
			fontSize: '22px',
			fontFamily: 'EPO-DS-Custom-Icon-Font-V2 !important',
			fontStyle: 'normal',
			lineHeight: 'var(--epods--line-height-h3)',
			verticalAlign: '0',
			cursor: disabled ? 'not-allowed' : 'pointer',
			textShadow:
				isfocused === 'true'
					? '0 0 2px var(--epods--color-border-focus), 0 0 2px var(--epods--color-border-focus), 0 0 2px var(--epods--color-border-focus), 0 0 2px var(--epods--color-border-focus), 0 0 3px var(--epods--color-border-focus), 0 0 3px var(--epods--color-border-focus)'
					: 'none',
		};
	},
);

export const CheckboxRoot = ($theme: any, props: any) => {
	const aligment = props.labelPlacement === 'left' ? { justifyContent: 'end' } : {};
	return {
		alignItems: 'center',
		...marginSize('0', '0', '0', '0'),
		...paddingSize('0', '0', '0', '0'),
		...aligment,
		position: 'relative' as any,
	};
};

export const CheckboxLabel = ($theme: typeof Theme, $disabled: boolean, props: IDummyProps) => {
	return {
		paddingLeft: props.children ? 'var(--epods--space-inset-s)' : '0',
		alignItems: 'center',
		overflow: 'hidden',
		whiteSpace: 'nowrap' as 'nowrap',
		textOverflow: 'ellipsis',

		fontFamily: 'var(--epods--font-base)',

		color: $disabled ? 'var(--epods--color-text-disabled)' : 'var(--epods--checkbox-label-color)',
		fontSize: CheckBoxFontSize[props.size || 'default'],
		lineHeight: CheckBoxLineHeight[props.size || 'default'],
	};
};

export const CheckboxInput = ($theme: any, $disabled: boolean) => {
	return {
		cursor: $disabled ? 'not-allowed' : 'pointer',
		height: 'var(--epods--checkbox-size-base)',
		width: 'var(--epods--checkbox-size-base)',
		zIndex: 2,
	};
};

export enum State {
	CHECKED = 'CHECKED',
	UNCHECKED = 'UNCHECKED',
}
export enum FormState {
	DEFAULT = 'DEFAULT',
	ERROR = 'ERROR',
}
export enum PseudoState {
	DEFAULT = 'DEFAULT',
	HOVER = 'HOVER',
	DISABLED = 'DISABLED',
}

export const ALL_STYLES: any = {
	//Record<State, Record<FormState, Record<PseudoState, StyleObject>>> = { // TODO: check
	[State.CHECKED]: {
		[FormState.DEFAULT]: {
			[PseudoState.DEFAULT]: {
				backgroundColor: 'var(--epods--checkbox-tickFillSelected)',
				...borderColor('var(--epods--checkbox-tickFillSelected)'),
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			},
			[PseudoState.HOVER]: {
				// Not on DS (copy of default)
				backgroundColor: 'var(--epods--checkbox-tickFillSelected)',
				...borderColor('var(--epods--checkbox-tickFillSelected)'),
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			},
			[PseudoState.DISABLED]: {
				backgroundColor: 'var(--epods--checkbox-tickFillSelectedDisabled)',
				...borderColor('var(--epods--color-border-light)'),
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			},
		},
		[FormState.ERROR]: {
			[PseudoState.DEFAULT]: {
				backgroundColor: 'var(--epods--checkbox-tickFillError)',
				...borderColor('var(--epods--checkbox-tickBorderError)'),
				// not in DS
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			},
			[PseudoState.HOVER]: {
				// not in DS
				backgroundColor: 'var(--epods--checkbox-tickFillError)',
				...borderColor('var(--epods--checkbox-tickBorderError)'),
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			},
			[PseudoState.DISABLED]: {
				// not in DS
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			},
		},
	},
	[State.UNCHECKED]: {
		[FormState.DEFAULT]: {
			[PseudoState.DEFAULT]: {
				backgroundColor: 'var(--epods--checkbox-tickFill)',
				...borderColor('var(--epods--checkbox-tickBorder)'),
				boxShadow: 'var(--epods--color-bg) 0px 0px 0px 2.01px inset',
			},
			[PseudoState.HOVER]: {
				backgroundColor: 'var(--epods--checkbox-tickFillHover)',
				...borderColor('var(--epods--checkbox-tickBorder)'),
				boxShadow: 'var(--epods--color-bg) 0px 0px 0px 2.01px inset',
			},
			[PseudoState.DISABLED]: {
				backgroundColor: 'var(--epods--color-bg-inactive-boxes-disabled)',
				...borderColor('var(--epods--checkbox-tickBorderDisabled)'),
				boxShadow: 'var(--epods--color-bg) 0px 0px 0px 2.01px inset',
			},
		},
		[FormState.ERROR]: {
			[PseudoState.DEFAULT]: {
				backgroundColor: 'var(--epods--checkbox-tickFill)',
				...borderColor('var(--epods--checkbox-tickBorderError)'),
				boxShadow: 'var(--epods--color-bg) 0px 0px 0px 2.01px inset',
			},
			[PseudoState.HOVER]: {
				backgroundColor: 'var(--epods--checkbox-tickFillHover)',
				...borderColor('var(--epods--checkbox-tickBorderError)'),
				boxShadow: 'var(--epods--color-bg) 0px 0px 0px 2.01px inset',
			},
			[PseudoState.DISABLED]: {
				// Not on DS
			},
		},
	},
};

export const CheckboxCheckmark = (
	$theme: typeof Theme,
	$isFocused: any,
	$isHovered: any,
	$isActive: any,
	$disabled: any,
	$checked: any,
	$isIndeterminate: any,
	props: any,
) => {
	const state = $checked || $isIndeterminate ? State.CHECKED : State.UNCHECKED;
	const formState = props.error ? FormState.ERROR : FormState.DEFAULT;
	const pseudoState = $disabled ? PseudoState.DISABLED : $isHovered ? PseudoState.HOVER : PseudoState.DEFAULT;
	const styles = ALL_STYLES[state][formState][pseudoState];

	return {
		// override for base web defaults
		...marginSize('0', '0', '0', '0'),
		height: 'var(--epods--checkbox-size-base)',
		width: 'var(--epods--checkbox-size-base)',
		...borderStyle('solid'),
		...borderWidth('1px'),
		...borderColor('transparent'),
		...styles,
		// if it has an error, override focus ring color
		...(props.error
			? {
					'--epods--color-bg-primary': 'var(--epods--color-bg-toggle-error)',
			  }
			: {}),
		...(!$disabled && $isFocused
			? $checked || $isIndeterminate
				? // if checked, add the focusring with gap, otherwise, merge inner box
				  // shadow on the focusRingNGap
				  focusRing
				: {
						...focusRingNoGap,
						boxShadow: `${styles.boxShadow}, ${focusRingNoGap.boxShadow}`,
				  }
			: {}),
	};
};
